<script setup lang="ts">
import '@unocss/reset/tailwind.css'

const { locale, t } = useI18n()

// carries the hreflang alternates and the canonical link for the current route
const i18nHead = useLocaleHead()

// 1
const showMenu = useState('showMenu', () => false)
const showSearch = useState('showSearch', () => false)

// 2
const globalPages = useState('globalPages', () => null)

const { data: globalPagesData } = await useAsyncData(
  () => `global-pages-${locale.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: ManyGlobalPagesQuery,
      variables: { locale: locale.value },
    },
  })
)

watch(globalPagesData, () => {
  globalPages.value = globalPagesData.value?.docs || []
}, { immediate: true })

// 2
const globalPageGroups = useState('globalPageGroups', () => null)

const { data: globalPageGroupsData } = await useAsyncData(
  () => `global-page-groups-${locale.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: ManyPageGroupsQuery,
      variables: { locale: locale.value, limit: 100 },
    },
  })
)

watch(globalPageGroupsData, () => {
  globalPageGroups.value = globalPageGroupsData.value?.docs || []
}, { immediate: true })

// 2
// every person name rendered anywhere on the site links only when the person is
// in this set (see `shared/person-link.vue`), so it is loaded app-wide
const linkablePeopleSlugs = useLinkablePeopleSlugs()

const { data: linkablePeopleData } = await useAsyncData(
  () => `linkable-people-${locale.value}`,
  () => $fetch<string[]>('/api/linkable-people', {
    query: { locale: locale.value },
  })
)

watch(linkablePeopleData, () => {
  linkablePeopleSlugs.value = new Set(linkablePeopleData.value ?? [])
}, { immediate: true })

// 3
const menuData = useState('menuData', () => null)

const { data: menuDataAsync, error } = await useAsyncData<Menu>(
  () => `menu-${locale.value}`,
  () => $fetch<Menu>('/api/payload-gql', {
    method: 'POST',
    body: {
      query: GlobalMenuQuery,
      variables: { locale: locale.value },
    },
  })
)

watch(menuDataAsync, () => {
  menuData.value = menuDataAsync.value || null
}, { immediate: true })

// 3
const sharedData = useState('sharedData', () => null)

const { data: sharedAsyncData, error: sharedError } = await useAsyncData(
  () => `global-data-${locale.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: GlobalSharedQuery,
      variables: { locale: locale.value },
    },
  })
)

watch(sharedAsyncData, () => {
  sharedData.value = sharedAsyncData.value || null
}, { immediate: true })

// 4
const runtimeConfig = useRuntimeConfig()

// the site is closed to crawlers until launch, see `seoIndexable` in nuxt.config
const isIndexable = computed(() => runtimeConfig.public.seoIndexable === true)

const siteName = useSiteName()

useHead(computed(() => ({
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${titleChunk} - ${siteName.value}` : siteName.value
  },
  meta: [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
    },
    {
      name: 'robots',
      content: isIndexable.value ? 'index, follow' : 'noindex, nofollow'
    },
    ...(i18nHead.value?.meta || [])
  ],
  link: [
    ...(i18nHead.value?.link || []),
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-white.png', media: '(prefers-color-scheme: dark)' },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-black.png', media: '(prefers-color-scheme: light)' },
  ],
  htmlAttrs: {
    lang: locale.value,
    dir: i18nHead.value?.htmlAttrs?.dir
  },
  bodyAttrs: {
    class: 'font-ateliers-sans',
  }
})))

// the site-wide floor every page's own tags sit on: a page that sets nothing
// still gets the shared global's description and cover image (see
// `composables/page-seo.ts`)
usePageSeo()

useSeoMeta({
  ogImage: '/seo-image.png'
})
</script>

<template>
  <!-- The column is pinned to the viewport rather than left implicit: an `auto`
       column sizes to max-content, so anything that writes a pixel width on
       itself (Swiper pins one on every slide) would widen the whole page and
       then be re-measured against that wider page — a ratchet that never
       shrinks back on resize. -->
  <div class="text-d relative grid grid-cols-[minmax(0,1fr)] grid-rows-[1fr_auto] gap-y-4 min-h-screen">
    <NuxtLoadingIndicator
      color="var(--color--accent)"
      :height="2"
    />

    <!-- SiteSearch and SiteMenu stay mounted (SiteMenu preloads the menu
         images) and own their backdrop and open/close transition -->
    <SiteSearch />

    <SiteMenu />

    <!-- a person opened from any page but the people list stacks on that page -->
    <SitePersonOverlay />

    <NuxtPage />

    <SiteFooter />

    <!-- <ClientOnly>
      <transition name="slide-from-bottom">
        <SiteGtag />
      </transition>
    </ClientOnly> -->
  </div>
</template>

<style>
html {
  font-family: helvetica;

  /* the header's search/menu buttons are pinned to the viewport while the rest
     of the page sits in flow, so anything that adds or removes the classic
     scrollbar — a breadcrumb wrapping to a second line and pushing the page
     past the viewport, or `useScrollLock` in page/modal.vue — shifts them
     against the content. Reserving the gutter up front keeps both edges put. */
  scrollbar-gutter: stable;
}

img {
  overflow-clip-margin: content-box;
  overflow: clip;
}

button:focus-visible,
button:focus {
  outline: none;
}

* {
  -webkit-touch-callout: none;
}

a[x-apple-data-detectors] {
  color: inherit !important;
  text-decoration: none !important;
  font-size: inherit !important;
  font-weight: inherit !important;
}

button,
[role="button"],
a {
  touch-action: manipulation;
  text-align: left;
}

a, button {
  text-decoration-skip-ink: auto;          /* For Chrome, Firefox */
  -webkit-text-decoration-skip: auto;    
}

a:focus, button:focus {
  outline: none;
}
</style>
